// API service для получения данных об автомобилях
import { config } from '../config/runtimeConfig.js';
import { carTitle } from '../utils/carTitle';
import { rebaseDemoAssets } from '../utils/demoAssets.js';
import { getAttribution } from './attributionManager';

/**
 * Getter'ы для динамического чтения config
 * ВАЖНО: Не используем const для присваивания, так как config читается динамически
 * через window.runtimeConfig, который устанавливается в widget.jsx после загрузки модулей
 */

// Экспортируем getter'ы для использования в других модулях (например, browser control)
export const getInventoryId = () => config.INVENTORY_ID;
export const getOrganizationId = () => config.ORGANIZATION_ID;
export const getInventorySlug = () => config.INVENTORY_SLUG;

// Legacy exports для обратной совместимости (динамические getters через Object.defineProperty)
// Эти экспорты позволяют использовать INVENTORY_ID, ORGANIZATION_ID, INVENTORY_SLUG как раньше
export const INVENTORY_ID = { toString: () => config.INVENTORY_ID, valueOf: () => config.INVENTORY_ID };
export const ORGANIZATION_ID = { toString: () => config.ORGANIZATION_ID, valueOf: () => config.ORGANIZATION_ID };
export const INVENTORY_SLUG = { toString: () => config.INVENTORY_SLUG, valueOf: () => config.INVENTORY_SLUG };

// Маппинг французских bodyStyle из API в английские для UI
const BODY_STYLE_MAPPING = {
  'Berline': 'Sedan',
  'Berline (4 dr)': 'Sedan',
  'Coupé': 'Coupe',
  'Cabriolet': 'Convertible',
  'Décapotable (2 dr)': 'Convertible',
  'Voiture à hayon': 'Hatchback',
  'Voiture à hayon (3 portes)': 'Hatchback',
  'Crossover': 'Crossover',
  'SUV': 'SUV',
  'Break': 'Wagon',
  'Monospace': 'Van',
  'Ludospace': 'Van',
  'Fourgon': 'Van',
  'Pick-Up': 'Pickup',
  'Utilitaire': 'Commercial',
  '': 'N/A'
};

/**
 * Нормализует и переводит bodyStyle в стандартное английское название.
 * Обрабатывает значения с запятыми - заменяет ", " на пробел для объединённых типов.
 * Также переводит французские названия в английские.
 * @param {string} bodyStyle - Название из API (может содержать запятую)
 * @returns {string} Нормализованное английское название для UI
 */
const translateBodyStyle = (bodyStyle) => {
  if (!bodyStyle) return 'N/A';

  // Если значение содержит запятую - заменяем её на пробел
  // Примеры: "Coupe, SUV" -> "Coupe SUV", "SUV, Crossover" -> "SUV Crossover"
  const normalizedValue = bodyStyle.includes(',')
    ? bodyStyle.replace(/,\s*/g, ' ').trim()
    : bodyStyle;

  return BODY_STYLE_MAPPING[normalizedValue] || normalizedValue || 'N/A';
};

/**
 * Один запрос инвентаря на всех, кто просит его одновременно (#1469).
 *
 * На главной Andys инвентарь при загрузке просят и лендинг, и mini-catalog —
 * у каждого свой CarsProvider, и каталог (582 КБ JSON на проде) качался
 * дважды. Параллельные вызовы с тем же URL получают один и тот же запрос.
 * После ответа или ошибки запись удаляется: следующая загрузка снова идёт в
 * сеть, а неудачная не застревает.
 */
const inflightInventory = new Map();

const loadInventoryJson = (inventoryUrl) => {
  let pending = inflightInventory.get(inventoryUrl);
  if (!pending) {
    pending = fetch(inventoryUrl, { headers: { accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .finally(() => inflightInventory.delete(inventoryUrl));
    inflightInventory.set(inventoryUrl, pending);
  }
  return pending;
};

/**
 * Получить список всех автомобилей из API
 * Поддерживает два формата:
 * - b2b-api: /api/dealer-inventory/org/{organizationId}/{inventorySlug}
 * - car-frontend-api (legacy): /dealer-inventory/{inventoryId}
 * @returns {Promise<Array>} Массив автомобилей
 */
export const fetchCarInventory = async () => {
  try {
    // Читаем config динамически каждый раз (для поддержки widget mode)
    const apiBaseUrl = config.CAR_API_URL;
    const organizationId = config.ORGANIZATION_ID;
    const inventorySlug = config.INVENTORY_SLUG;
    const inventoryId = config.INVENTORY_ID;

    console.log('[API] Config values:', { apiBaseUrl, organizationId, inventorySlug, inventoryId });

    // Autodis (org 4 / demo-autodis) больше НЕ short-circuit'ится на бандловый
    // JSON (#1034 Phase 3c, блок C1): каталог отдаёт b2b-api из источника
    // demo-autodis (те же данные — дифф 3b 9⇄9 чистый; лендинговые поля
    // imagePosition/heroImage/landscapeImage едут через attributes open-bag).
    // Эталонная копия каталога живёт в apps/b2b-api (миграция 1831 +
    // static-fallback), бандловая копия удалена.

    // Определяем URL в зависимости от конфигурации
    // Если есть ORGANIZATION_ID и INVENTORY_SLUG - используем b2b-api формат
    // Иначе fallback на legacy car-frontend-api формат
    let inventoryUrl;
    // Добавляем /api если его нет в apiBaseUrl
    const apiPath = apiBaseUrl.endsWith('/api') ? '' : '/api';
    // Используем != null для корректной проверки (пропускает только null и undefined)
    if (organizationId != null && inventorySlug != null) {
      // b2b-api формат: /api/dealer-inventory/org/{orgId}/{slug}
      inventoryUrl = `${apiBaseUrl}${apiPath}/dealer-inventory/org/${organizationId}/${inventorySlug}`;
      console.log('[API] Using b2b-api inventory endpoint:', inventoryUrl);
    } else {
      // Legacy car-frontend-api формат
      inventoryUrl = `${apiBaseUrl}${apiPath}/dealer-inventory/${inventoryId}`;
      console.log('[API] Using legacy car-frontend-api inventory endpoint:', inventoryUrl);
    }

    const result = await loadInventoryJson(inventoryUrl);

    // Преобразуем данные API в формат приложения с обработкой отсутствующих данных
    return result.data.map(car => ({
      ...car,
      // Конвертируем ID в строку для корректного роутинга
      id: String(car.id),
      // Если изображений нет, передаем пустой массив (компонент покажет placeholder)
      images: Array.isArray(car.images) && car.images.length > 0 ? rebaseDemoAssets(car.images) : [],
      // Цена может быть 0 (обработается в компонентах как "Contact for price")
      price: typeof car.price === 'number' ? car.price : 0,
      // Пробег может быть 0 (обработается в компонентах)
      mileage: typeof car.mileage === 'number' ? car.mileage : 0,
      // Убеждаемся что features это массив
      features: Array.isArray(car.features) && car.features.length > 0 ? car.features : [],
      // Убеждаемся что description это строка, если нет - создаем из данных
      description: car.description && car.description.trim() !== ''
        ? car.description
        : carTitle(car),
      // Убеждаемся что все обязательные поля существуют
      make: car.make || 'Unknown',
      model: car.model || 'Model',
      // #1206: NO fallback for year/fuelType/transmission — a value the dealer
      // hasn't entered must stay absent. Substituting one here (year -> current
      // year, fuel -> 'N/A') would re-fabricate exactly what the API-side mapper
      // stopped inventing, and would defeat the guards that depend on it being absent.
      year: car.year,
      fuelType: car.fuelType,
      transmission: car.transmission,
      color: car.color || null,
      // Engine specs
      engineDisplacement: car.engineDisplacement || null,
      enginePower: car.enginePower || null,
      // Переводим bodyStyle из API (французский) в английское название
      bodyStyle: translateBodyStyle(car.bodyStyle),
      // Menu index для дефолтной сортировки
      menuIndex: car.menuIndex,
      // Registration status (если нет - машина не зарегистрирована, показываем +reg)
      registrationStatus: car.registrationStatus,
      // Old price для отображения скидки (из Google Sheets поле old_price)
      // API возвращает oldPrice (camelCase), не old_price
      oldPrice: typeof car.oldPrice === 'number' && car.oldPrice > 0 ? car.oldPrice : null,
      // Duty Free fields from inventory
      dutyFree: car.dutyFree || false,
      dutyFreePrice: typeof car.dutyFreePrice === 'number' && car.dutyFreePrice > 0 ? car.dutyFreePrice : null,
      // VIN / Chassis ID — spread above already carries it; explicit for clarity ('' → null)
      vin: car.vin || null,
      // Registration month (1–12) — drives the MM/YYYY display combo with `year`
      registrationMonth: car.registrationMonth ?? null,
      // Delivery availability (#1219) — canonical 'immediate' | 'YYYY-MM' | null.
      // Rendered via formatDeliveryDate(); null keeps the VDP row hidden.
      deliveryDate: car.deliveryDate || null,
    }));
  } catch (error) {
    console.error('Error fetching car inventory:', error);
    throw error;
  }
};

/**
 * Поиск автомобилей через Meilisearch (через b2b-api proxy)
 * GET /api/search?q=...&organizationId=...&inventorySlug=...&filters=...&sort=...
 * @param {string} query - Поисковый запрос
 * @param {Object} [options] - Опции поиска
 * @param {string} [options.filters] - Meilisearch filter expression
 * @param {string} [options.sort] - Meilisearch sort expression (e.g. "price:asc")
 * @param {number} [options.limit] - Максимальное количество результатов
 * @param {number} [options.offset] - Смещение для пагинации
 * @param {boolean} [options.force] - Обойти legacy `WIDGET_SEARCH_MODE === 'disabled'`
 *   short-circuit. Используется Search registry component (search-only embed)
 *   где marker presence = explicit opt-in и global `features.showSearchBar`
 *   flag нерелевантен. См. issue #932 (decoupling).
 * @returns {Promise<Object>} Результаты поиска { hits, query, processingTimeMs, estimatedTotalHits, facetDistribution, available, mode }
 *
 * Иерархия приоритетов (важно):
 *   1. b2b-api (`MEILI_SEARCH_MODE` server env) — ФИНАЛЬНЫЙ AUTHORITY.
 *      `mode` поле в response — единственная истина об эффективном режиме.
 *   2. b2b-demo widget config (`WIDGET_SEARCH_MODE`, computed из
 *      `showSearchBar` + `searchBarMode`) — лишь client request: решает,
 *      делать ли запрос вообще и слать ли `?hybrid=true`.
 *
 * Поведение по `WIDGET_SEARCH_MODE`:
 *   - 'disabled' → SearchBar выключен на уровне виджета (showSearchBar=false);
 *                  фронтенд stub-ит ответ без сетевого вызова. Защитная мера —
 *                  обычно UI компонент уже не рендерится в этом случае.
 *                  Игнорируется при `options.force=true` (Search registry).
 *   - 'keyword'  → шлём обычный keyword запрос.
 *   - 'hybrid'   → шлём `?hybrid=true&semanticRatio=0.5`. Дальше слово за
 *                  сервером: `MEILI_SEARCH_MODE=keyword` → server вернёт
 *                  `mode: 'keyword'`; `=disabled` → `mode: 'disabled'`;
 *                  `=hybrid` → `mode: 'hybrid'`.
 *
 * UI должен ориентироваться на `mode` из ответа, а не на `WIDGET_SEARCH_MODE`.
 */
export const searchCarInventory = async (query, options = {}) => {
  // Если caller явно форсирует (Search registry component, search-only embed),
  // используем `SEARCH_BAR_MODE_RAW` который читает `mount.searchBarMode`
  // напрямую, минуя `SHOW_SEARCH_BAR` short-circuit. Это нужно для full
  // decoupling: admin может задать `searchBarMode='hybrid'` для search-only
  // embed БЕЗ обязательного включения `showSearchBar=true` (которое
  // активирует in-catalog SearchBar в `<CarsPage>` — нежелательное побочное
  // включение). Disabled-режим при force никогда не возвращается — иначе
  // bypass теряет смысл. См. issue #932.
  const requestedMode = options.force
    ? config.SEARCH_BAR_MODE_RAW
    : config.WIDGET_SEARCH_MODE;

  // Defensive stub: SearchBar UI отсутствует, но кто-то всё же вызвал
  // searchCarInventory напрямую. Возвращаем тот же shape, что и server
  // вернул бы при MEILI_SEARCH_MODE=disabled, не делая сетевого запроса.
  if (requestedMode === 'disabled') {
    return {
      hits: [],
      query,
      processingTimeMs: 0,
      estimatedTotalHits: 0,
      facetDistribution: {},
      available: false,
      mode: 'disabled',
    };
  }

  try {
    const apiBaseUrl = config.CAR_API_URL;
    const organizationId = config.ORGANIZATION_ID;
    const inventorySlug = config.INVENTORY_SLUG;
    const apiPath = apiBaseUrl.endsWith('/api') ? '' : '/api';

    const params = new URLSearchParams({ q: query });

    if (organizationId != null) {
      params.set('organizationId', String(organizationId));
    }
    if (inventorySlug != null) {
      params.set('inventorySlug', String(inventorySlug));
    }
    if (options.filters) {
      params.set('filters', options.filters);
    }
    if (options.sort) {
      params.set('sort', options.sort);
    }
    if (options.limit) {
      params.set('limit', String(options.limit));
    }
    if (options.offset) {
      params.set('offset', String(options.offset));
    }
    if (requestedMode === 'hybrid') {
      params.set('hybrid', 'true');
      // Balanced default — mirrors `meiliDefaultSemanticRatio` in
      // apps/b2b-api/src/app/app.config.ts and `DEFAULT_SEMANTIC_RATIO` in
      // apps/b2b_agent/utils/meilisearch/config.py. Keep all three in sync.
      params.set('semanticRatio', '0.5');
    }

    const searchUrl = `${apiBaseUrl}${apiPath}/search?${params.toString()}`;
    console.log(`[API] Meilisearch query (requested=${requestedMode}):`, searchUrl);

    const response = await fetch(searchUrl, {
      headers: { 'accept': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Search HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // Нормализуем hits так же как fetchCarInventory
    if (result.hits) {
      result.hits = result.hits.map(car => ({
        ...car,
        id: String(car.id),
        images: Array.isArray(car.images) && car.images.length > 0 ? rebaseDemoAssets(car.images) : [],
        price: typeof car.price === 'number' ? car.price : 0,
        mileage: typeof car.mileage === 'number' ? car.mileage : 0,
        features: Array.isArray(car.features) && car.features.length > 0 ? car.features : [],
        description: car.description && car.description.trim() !== ''
          ? car.description
          : carTitle(car),
        make: car.make || 'Unknown',
        model: car.model || 'Model',
        // #1206: NO fallback for year/fuelType/transmission — a value the dealer
        // hasn't entered must stay absent. Substituting one here (year -> current
        // year, fuel -> 'N/A') would re-fabricate exactly what the API-side mapper
        // stopped inventing, and would defeat the guards that depend on it being absent.
        year: car.year,
        fuelType: car.fuelType,
        transmission: car.transmission,
        color: car.color || null,
        engineDisplacement: car.engineDisplacement || null,
        enginePower: car.enginePower || null,
        bodyStyle: translateBodyStyle(car.bodyStyle),
        menuIndex: car.menuIndex,
        registrationStatus: car.registrationStatus,
        oldPrice: typeof car.oldPrice === 'number' && car.oldPrice > 0 ? car.oldPrice : null,
        dutyFree: car.dutyFree || false,
        dutyFreePrice: typeof car.dutyFreePrice === 'number' && car.dutyFreePrice > 0 ? car.dutyFreePrice : null,
        // VIN / Chassis ID — spread above already carries it; explicit for clarity ('' → null)
        vin: car.vin || null,
        // Registration month (1–12) — drives the MM/YYYY display combo with `year`
        registrationMonth: car.registrationMonth ?? null,
        // Delivery availability (#1219). Meilisearch stores '' for "not stated";
        // normalise to null so both paths hand the UI the same shape.
        deliveryDate: car.deliveryDate || null,
      }));
    }

    return result;
  } catch (error) {
    console.error('[API] Meilisearch search failed:', error);
    // Network failure shape mirrors the server's `mode: 'disabled'` stub so
    // UI consumers can dispatch on `response.mode` uniformly without having
    // to special-case missing field for the network-error path.
    return {
      hits: [],
      query,
      processingTimeMs: 0,
      estimatedTotalHits: 0,
      facetDistribution: {},
      available: false,
      mode: 'disabled',
    };
  }
};

/**
 * Отправить лид из формы виджета в CRM (Check Availability / Finance Application / Test Drive).
 *
 * Идёт параллельно с основным submit в MODX Formit (если он сконфигурирован) —
 * НЕ блокирует поток доставки email. Если `captureFormsToCrm` выключено в
 * widget config или нет frontend-token — функция тихо возвращает `null`
 * (caller не должен show-stopp-ить форму).
 *
 * Backend endpoint: POST /api/widget/lead-capture (authenticated via
 * X-Widget-Token header — тот же token, что для публичного widget config).
 *
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.phone
 * @param {string} [payload.email]
 * @param {'check_availability'|'finance'|'test_drive'} payload.formType
 * @param {string} [payload.carOfferId]
 * @param {string} [payload.carDescription]
 * @param {string} [payload.sourceUrl]
 * @param {string} [payload.vehicleInterest]
 * @param {string} [payload.message]
 * @param {string} [payload.sessionId] - Browsing session id → CRM behavior linkage (#975)
 * @param {string} [payload.visitorId] - Persistent visitor id → CRM behavior linkage (#975)
 * @param {Object} [payload.attribution] - First-touch marketing attribution (#1467)
 * @param {Object} [payload.extra]
 * @param {{draftId: string, completedSteps: number, totalSteps: number}} [payload.progress]
 *   - Multi-screen forms (#1469): screens finished so far (a partial lead).
 * @param {Object} [options]
 * @param {boolean} [options.keepalive] - Let the request outlive the page: a
 *   partial lead is sent as the visitor leaves (#1469).
 * @returns {Promise<{leadId: string, created: boolean} | null>}
 */
export const submitLeadFromForm = async (payload, { keepalive = false } = {}) => {
  try {
    if (!config.CAPTURE_FORMS_TO_CRM) {
      return null;
    }
    const token = config.FRONTEND_TOKEN;
    if (!token) {
      console.debug('[CRM] submitLeadFromForm skipped: no frontend token');
      return null;
    }

    const apiBaseUrl = config.CAR_API_URL;
    if (!apiBaseUrl) {
      console.debug('[CRM] submitLeadFromForm skipped: no CAR_API_URL');
      return null;
    }
    const apiPath = apiBaseUrl.endsWith('/api') ? '' : '/api';
    const endpoint = `${apiBaseUrl}${apiPath}/widget/lead-capture`;

    // Marketing attribution is attached HERE rather than in each form (#1467).
    // There are two production callers and the second one — the Andys landing
    // page — was missed when this was bolted onto LeadFormEmbed alone, which
    // is the structurally worst place to miss: it is the page paid traffic
    // lands on. Attaching it in the transport means a new form cannot forget.
    const body = { ...payload, attribution: payload.attribution ?? getAttribution() };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Widget-Token': token,
      },
      body: JSON.stringify(body),
      keepalive,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.warn(
        `[CRM] submitLeadFromForm failed: HTTP ${response.status} — ${text.slice(0, 200)}`,
      );
      return null;
    }

    const result = await response.json();
    console.debug('[CRM] lead captured:', result);
    return result;
  } catch (error) {
    // Фатальные ошибки сети — не прокидываем выше, чтобы не сломать MODX flow.
    console.warn('[CRM] submitLeadFromForm error:', error?.message || error);
    return null;
  }
};
